import { useState } from 'react';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import { useNavigate, useParams } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Alert, Button, Typography } from '@mui/material';
import CostOfWork from './CostOfWork';
import { handleInsertSubcodeDocs } from '../uploadSubcodeDocs';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import {
  resetUccForm,
  updateChimney
} from 'src/redux-store/slices/constructionFormSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import Summary from './Summary';
import getEnvVars from 'src/config/env';

// Interface for tab panel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Custom tab panel component
function CustomTabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Function to generate accessibility props for tabs
type A11yProps = (index: number) => { id: string; 'aria-controls': string };
const a11yProps: A11yProps = (index) => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`
});

const ChimneySubcode = ({
  applicationId,
  UCCFormShareID = 0,
  townshipID = 0,
  setExpanded,
  isGuest = false,
  fetchFormStatuses
}) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(0);
  const { applicationID, townshipId } = useParams();
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const subCodesFormData = useSelector(
    (state: RootState) => state.constructionFormSlice.Chimney
  );
  const { socketUrl } = getEnvVars();
  // Handles form field changes and updates the redux store
  const handleChange = (val: string, e: any) => {
    const { name } = e;
    dispatch(updateChimney({ [name]: val }));
  };
  const navigate = useNavigate();

  // List of form fields
  const allFields: Fields[] = [
    {
      fieldName: 'ContractorName',
      initialValue: subCodesFormData?.ContractorName || '',
      fieldLabel: 'Contractor Name',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorEmail',
      initialValue: subCodesFormData?.ContractorEmail || '',
      fieldLabel: 'Contractor Email Address',
      fieldType: 'email',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorPhoneNumber',
      initialValue: subCodesFormData?.ContractorPhoneNumber || '',
      fieldLabel: 'Contractor Phone Number',
      fieldType: 'mobile',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorAddress',
      initialValue: subCodesFormData?.ContractorAddress || '',
      fieldLabel: 'Contractor Address',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'UtilityCompany',
      initialValue: subCodesFormData?.UtilityCompany || '',
      fieldLabel: 'Utility Company',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'FederalID',
      initialValue: subCodesFormData?.FederalID || '',
      fieldLabel: 'Federal Employer ID Number (EIN)',
      fieldType: 'number',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorLicenseNumber',
      initialValue: subCodesFormData?.ContractorLicenseNumber || '',
      fieldLabel: 'Contractor License Number',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ExpiryDate',
      initialValue: subCodesFormData?.ExpiryDate || '',
      fieldLabel: 'Contractor License Expiration Date',
      required: true,
      fieldType: 'date',
      dependency: (val: string) => dispatch(updateChimney({ ExpiryDate: val }))
    },
    {
      fieldName: 'Manufacturer',
      initialValue: subCodesFormData?.Manufacturer || '',
      fieldLabel: 'Manufacturer',
      fieldType: 'text',
      required: true,
      dependency: handleChange,
      heading:
        'If a chimney liner is being installed, all documentation on the liner must accompany the Permit application.'
    },
    {
      fieldName: 'Model',
      initialValue: subCodesFormData?.Model || '',
      fieldLabel: 'Model',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ULListing',
      initialValue: subCodesFormData?.ULListing || '',
      fieldLabel: 'UL Listing',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'SizeofApplianceVent',
      initialValue: subCodesFormData?.SizeofApplianceVent || '',
      fieldLabel: 'Size of Appliance Vent',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'SizeofLiner',
      initialValue: subCodesFormData?.SizeofLiner || '',
      fieldLabel: 'Size of Liner',
      fieldType: 'number',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'HeightOfChimney',
      initialValue: subCodesFormData?.HeightOfChimney || '',
      fieldLabel: 'Height of Chimney',
      fieldType: 'number',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'LengthOfConnector',
      initialValue: subCodesFormData?.LengthOfConnector || '',
      fieldLabel: 'Length of Connector',
      fieldType: 'number',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'VentConnectorRise',
      initialValue: subCodesFormData?.VentConnectorRise || '',
      fieldLabel: 'Vent Connector Rise',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ApplianceVent',
      initialValue: subCodesFormData?.ApplianceVent || '',
      fieldLabel: 'How does the appliance vent ?',
      fieldType: 'select',
      options: [
        'Natural Draft',
        'Power Vent',
        'Fan-assisted',
        'Direct Vent',
        'Other'
      ],
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'MaterialOfLiner',
      initialValue: subCodesFormData?.MaterialOfLiner || '',
      fieldLabel: 'Material of Liner',
      fieldType: 'select',
      options: ['Stainless Steel', 'Aluminum'],
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'DescriptionOfWork',
      initialValue: subCodesFormData?.DescriptionOfWork || '',
      fieldLabel: 'Description of Work',
      fieldType: 'note',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ChimneyDocumentFile',
      fieldLabel: 'Upload Document',
      initialValue: subCodesFormData?.ChimneyDocumentFile || '',
      fieldType: 'file',
      listUI: true,
      placement: 'start',
      dependency: handleChange,
      aboveParagraph: (
        <Typography>
          Please upload scanned copy or photo of the Federal Employer ID Number
          (EIN) license. Only image files (JPG, PNG, GIF, SVG, HEIC) and PDF
          files are allowed, with maximum file size of 10MB.
        </Typography>
      ),
      belowParagraph: subCodesFormData?.DocumentOfContractorIdentity && (
        <a
          target="_blank"
          rel="noreferrer"
          href={socketUrl + subCodesFormData?.DocumentOfContractorIdentity}
        >
          Federal Employer ID Number (EIN) license
        </a>
      )
    }
  ];

  async function insertChimneySubcode(payload: any) {
    const response = await httpRequest(
      'POST',
      `/construction/chimney/ucc/${applicationId}`,
      payload
    );
    if (response?.status === 'success') {
      toast.success(`Chimney UCC form created successfully.`);
      dispatch(
        updateChimney({
          UCCChimneyID: response?.data?.UCCChimneyID
        })
      );
      if (!townshipID) fetchFormStatuses();
      dispatch(resetUccForm());
      if (isGuest === true) {
        // For guest users, redirect to confirmation page
        // You may need to adjust the route based on your routing setup
        navigate('/confirmation', {
          state: {
            formType: 'Chimney UCC Form',
            email: payload.email
          },
          replace: true // This prevents going back with the browser back button
        });
      } else {
        // For regular users, just reset the form
        setValue(0);
      }
      // setLoading(false);
    } else {
      // setLoading(false);
      toast.error(response?.message);
    }
  }

  async function updateChimneySubcode(payload: any) {
    const response = await httpRequest(
      'PATCH',
      `/construction/chimney/ucc/${subCodesFormData?.UCCChimneyID}/${
        applicationId || applicationID
      }`,
      payload
    );
    if (response?.status === 'success') {
      toast.success(`Chimney UCC form updated successfully.`);
      dispatch(
        updateChimney({
          UCCChimneyID: response?.data?.UCCChimneyID
        })
      );
      if (!townshipID) fetchFormStatuses();
      dispatch(resetUccForm());
      setValue(0);
      // setLoading(false);
    } else {
      // setLoading(false);
      toast.error(response?.message);
    }
  }

  async function handleSaveForm() {
    let FileData = { FilePath: '' };
    if (subCodesFormData?.ChimneyDocumentFile) {
      FileData = await handleInsertSubcodeDocs(
        subCodesFormData?.ChimneyDocumentFile[0],
        'Construction',
        'Chimney'
      );
    }

    const payload = {
      ContractorName: subCodesFormData?.ContractorName,
      ContractorEmail: subCodesFormData?.ContractorEmail,
      ContractorPhoneNumber: subCodesFormData?.ContractorPhoneNumber,
      ContractorAddress: subCodesFormData?.ContractorAddress,
      UtilityCompany: subCodesFormData?.UtilityCompany,
      FederalID: subCodesFormData?.FederalID,
      ContractorLicenseNumber: subCodesFormData?.ContractorLicenseNumber,
      ExpiryDate: subCodesFormData?.ExpiryDate,
      DocumentOfContractorIdentity: FileData?.FilePath,
      DescriptionOfWork: subCodesFormData?.DescriptionOfWork,

      Manufacturer: subCodesFormData?.Manufacturer,
      Model: subCodesFormData?.Model,
      ULListing: subCodesFormData?.ULListing,
      MaterialOfLiner: subCodesFormData?.MaterialOfLiner,
      SizeofApplianceVent: subCodesFormData?.SizeofApplianceVent,
      SizeofLiner: subCodesFormData?.SizeofLiner,
      HeightOfChimney: subCodesFormData?.HeightOfChimney,
      LengthOfConnector: subCodesFormData?.LengthOfConnector,
      VentConnectorRise: subCodesFormData?.VentConnectorRise,
      ApplianceVent: subCodesFormData?.ApplianceVent,
      ReplacementType: subCodesFormData?.ReplacementType,
      ExistingVentOrChimneySize: subCodesFormData?.ExistingVentOrChimneySize,
      SignatureImage: subCodesFormData?.SignatureImage,
      TotalFees: subCodesFormData?.getAllFee?.totalFee,

      SelfFilled: userDetails?.UserID ? true : false,
      UserID: userDetails?.UserID || 0,
      WorkItems: subCodesFormData?.WorkItems || [],
      TownshipID: Number(townshipId || townshipID),
      UCCFormShareID: UCCFormShareID ? UCCFormShareID : null
    };
    if (subCodesFormData?.UCCChimneyID) {
      updateChimneySubcode(payload);
    } else insertChimneySubcode(payload);
    setValue(2);
  }

  function onNext() {
    setValue(value + 1);
  }

  function onBack() {
    setValue(value - 1);
  }

  // Add this function after your existing handleChange function
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
  setValue(newValue);
  };
  
  return (
    <>
      <Box sx={{ width: '100%' }}>
        {/* Tabs */}
        <Tabs value={value} onChange={handleTabChange} aria-label="Construction Permit Tabs">
          <Tab label="Chimney Subcode" {...a11yProps(0)} />
          <Tab label="Cost of Work" {...a11yProps(1)} />
          <Tab label="Summary of Costs" {...a11yProps(2)} />
        </Tabs>

        <Alert severity="info" sx={{ mt: 2 }}>
          Provide information for whoever will be performing Chimney work.
        </Alert>

        {/* Tab 1 - Chimney Subcode Form */}
        <CustomTabPanel value={value} index={0}>
          <FormikForm
            allFields={allFields}
            defaultEnabledButton={['Save']}
            buttonTitle={['Cancel', 'Save']}
            heading={'Construction Permit'}
            buttonPlacement={'right'}
            formMethod={onNext}
            handleOtherButton={() => setExpanded(false)}
          />
        </CustomTabPanel>

        {/* Tab 2 - Estimated Cost of Work Form */}
        <CustomTabPanel value={value} index={1}>
          <CostOfWork
            onBack={onBack}
            onNext={onNext}
            townshipID={townshipID}
            ReplacementType={subCodesFormData?.ReplacementType}
            ExistingVentOrChimneySize={
              subCodesFormData?.ExistingVentOrChimneySize
            }
          />
        </CustomTabPanel>

        {/* Tab 3 - Summary of Costs */}
        <CustomTabPanel value={value} index={2}>
          <Summary />

          {/* Form Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 3
            }}
          >
            <Button onClick={onBack} variant="contained" color="inherit">
              Back
            </Button>
            <Button onClick={handleSaveForm} variant="contained">
              {subCodesFormData?.UCCChimneyID ? 'Update' : 'Save'}
            </Button>
          </Box>
        </CustomTabPanel>
      </Box>
    </>
  );
};

// Add display name for debugging purposes
ChimneySubcode.displayName = 'ChimneySubcode';

export default ChimneySubcode;
